import { useState } from 'react'
import { useAccounts } from '@/features/transactions/hooks'
import { useBudgetMonth } from '@/features/budget/hooks'
import { currentYearMonth } from '@/features/budget/format'
import { CategoryDropdown } from '@/features/transactions/CategoryDropdown'
import type { Frequency, ScheduledTransaction, ScheduledType } from './types'

const FREQUENCIES: Frequency[] = ['weekly', 'biweekly', 'monthly', 'yearly']

interface Props {
  onSave: (s: ScheduledTransaction) => void
  onCancel: () => void
  busy: boolean
}

const inputCls =
  'rounded-md border border-zinc-300 bg-transparent px-2 py-1.5 text-sm dark:border-zinc-700'

export function ScheduledForm({ onSave, onCancel, busy }: Props) {
  const accounts = useAccounts()
  const { year, month } = currentYearMonth()
  const budgetMonth = useBudgetMonth(year, month)
  const [payeeName, setPayeeName] = useState('')
  const [amount, setAmount] = useState('')
  const [type, setType] = useState<ScheduledType>('expense')
  const [frequency, setFrequency] = useState<Frequency>('monthly')
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10))
  const [accountId, setAccountId] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)

  const openAccounts = (accounts.data ?? []).filter((a) => !a.isHidden)
  const magnitude = Math.abs(Number(amount))
  const valid = accountId !== '' && Number.isFinite(magnitude) && magnitude > 0

  function submit() {
    const now = new Date().toISOString()
    const start = new Date(`${startDate}T12:00:00`).toISOString()
    onSave({
      id: crypto.randomUUID(),
      accountId,
      payeeName: payeeName.trim() || null,
      categoryId,
      amount: magnitude,
      memo: null,
      frequency,
      startDate: start,
      endDate: null,
      nextOccurrence: start,
      transactionType: type,
      transferAccountId: null,
      isEnabled: true,
      lastCreatedDate: null,
      createdAt: now,
      updatedAt: now,
    })
  }

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
      <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputCls}>
        <option value="">Account…</option>
        {openAccounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <input value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder="Payee" className={inputCls} />
      <div className="min-w-44">
        <CategoryDropdown groups={budgetMonth.data?.categoryGroups ?? []} value={categoryId} onChange={setCategoryId} />
      </div>
      <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" inputMode="decimal" className={`${inputCls} w-24`} />
      <select value={type} onChange={(e) => setType(e.target.value as ScheduledType)} className={inputCls}>
        <option value="expense">Expense</option>
        <option value="income">Income</option>
      </select>
      <select value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)} className={inputCls}>
        {FREQUENCIES.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
      <button type="button" disabled={!valid || busy} onClick={submit} className="rounded-md bg-zinc-900 px-3 py-1.5 text-sm text-white disabled:opacity-40 dark:bg-zinc-100 dark:text-zinc-900">
        Save
      </button>
      <button type="button" onClick={onCancel} className="rounded-md px-3 py-1.5 text-sm text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800">
        Cancel
      </button>
    </div>
  )
}
